<script setup lang="ts">
    import router from '@/router'
    import type { ErstelleNutzerRequest } from '@/js/models'

    type RegistrationForm = {
        email: string
        userEmail: string
        passwort: string
        passwortRepeat: string
    }

    const showPassword = ref(false)
    const showPasswordRepeat = ref(false)

    const { handleSubmit } = useForm<RegistrationForm>()

    const fields: {
        [Property in keyof RegistrationForm]: any
    } = {
        email: useField('email', (value) => !!value || 'E-Mail-Adresse muss angegeben werden.'),
        userEmail: useField('userEmail', () => true),
        passwort: useField<string>('passwort', (value) => {
            if (!value) {
                return 'Passwort muss angegeben werden.'
            }

            if (value.length < 8) {
                return 'Passwort muss mind. 8 Zeichen haben'
            }

            return true
        }),
        passwortRepeat: useField<string>('passwortRepeat', (value) => {
            if (value !== fields.passwort.value.value) {
                return 'Passwörter müssen überein stimmen.'
            }
            return true
        })
    }

    const isRegistrationSuccessful = ref(false)

    const nutzerStore = useNutzerStore()
    const alertStore = useAlertStore()

    const submitLoading = computed(() => nutzerStore.isMethodLoading('erstelleNutzer'))

    const submit = handleSubmit(async () => {
        try {
            let request: ErstelleNutzerRequest = {
                userEmail: fields.userEmail.value.value ?? '',
                email: String(fields.email.value?.value),
                passwort: fields.passwort.value?.value
            }
            await nutzerStore.erstelleNutzer(request)
            isRegistrationSuccessful.value = true
            alertStore.success('Die Registrierung war erfolgreich.')
        } catch (e: unknown) {
            console.error(e)
        }
    })
</script>

<template>
    <v-row justify="center" class="mt-6">
        <v-col cols="12" sm="8" md="6" lg="6" xl="4">
            <h1 class="text-center mb-6">Registrieren</h1>

            <v-form @submit.prevent="submit" :disabled="isRegistrationSuccessful">
                <!-- E-Mail -->
                <v-text-field
                    class="mb-4"
                    v-model.trim="fields.email.value.value"
                    label="E-Mail-Adresse"
                    name="email"
                    data-test="email"
                    :hide-details="!fields.email.errorMessage.value"
                    :error-messages="fields.email.errorMessage.value"
                    required
                    autocomplete="email"
                />

                <!-- Hidden Field -->
                <v-text-field
                    v-model.trim="fields.userEmail.value.value"
                    label="E-Mail-Adresse"
                    name="email"
                    data-test="userEmail"
                    :error-messages="fields.userEmail.errorMessage.value"
                    required
                    autocomplete="off"
                    class="d-none"
                />

                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            class="mb-4"
                            v-model.trim="fields.passwort.value.value"
                            label="Passwort"
                            :type="showPassword ? 'text' : 'password'"
                            name="password"
                            required
                            autocomplete="off"
                            data-test="password"
                            :hide-details="!fields.passwort.errorMessage.value"
                            :error-messages="fields.passwort.errorMessage.value"
                            :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append-inner="showPassword = !showPassword"
                        />
                    </v-col>

                    <v-col cols="12" sm="6">
                        <v-text-field
                            class="mb-4"
                            v-model.trim="fields.passwortRepeat.value.value"
                            label="Passwort wiederholen"
                            :type="showPasswordRepeat ? 'text' : 'password'"
                            name="passwordRepeat"
                            required
                            autocomplete="off"
                            data-test="passwordRepeat"
                            :hide-details="!fields.passwortRepeat.errorMessage.value"
                            :error-messages="fields.passwortRepeat.errorMessage.value"
                            :append-inner-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append-inner="showPasswordRepeat = !showPasswordRepeat"
                        />
                    </v-col>
                </v-row>
                <v-alert v-if="!isRegistrationSuccessful" class="w-100 text-muted mb-3">
                    <b>Hinweis:</b>
                    <ul class="mb-0 pl-5">
                        <li>
                            Nach dem Erstellen erhältst du eine E-Mail, um dein Konto zu aktivieren
                            und ein Passwort zu setzen.
                        </li>
                    </ul>
                </v-alert>

                <!-- Buttons -->
                <v-row>
                    <v-col cols="12">
                        <v-btn
                            type="submit"
                            color="#00527e"
                            class="mb-3"
                            block
                            :disabled="nutzerStore.isLoading || isRegistrationSuccessful"
                            :loading="submitLoading"
                            data-test="Account erstellen"
                        >
                            Account erstellen
                        </v-btn>
                        <v-alert v-if="isRegistrationSuccessful" class="mb-2" icon="mdi-check-circle-outline" type="success" prominent>
                            <b>Account erfolgreich erstellt</b>
                            <ul class="mb-0 pl-5">
                                <li>
                                    Du solltest eine Mail bekommen haben.
                                </li>
                                <li>
                                    Bitte schaue ggfs. auch in deinem Spam-Ordner nach.
                                </li>
                                <li>
                                    Alle weiteren Schritte stehen in der Mail.
                                </li>
                            </ul>
                        </v-alert>
                        <v-btn :to="{ name: 'login' }" variant="plain" color="#00527e" block>
                            Zurück zur Anmeldung
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </v-col>
    </v-row>
</template>
